import { WidgetDailyStatisticsResponse, WidgetStatistics, WidgetStatisticsResponse } from 'app/campaign/widgets.model';

/**
 * Migration adapter — DataPlatform widget-analytics responses → legacy TS shapes.
 *
 * Canonical event-type strings and field mappings are mirrored from the C#
 * provider at Widgets/src/libs/Triggerbee.Widgets.ApplicationServices/
 * WidgetAnalytics/DataPlatformWidgetStatsProvider.cs. Keep them in sync.
 *
 * EqualWeight is deliberately not produced — DataPlatform does not store the
 * equal-weight attribution model. Legacy revenue-graph series for EqualWeight*
 * are omitted from the daily-revenue mapping for the same reason.
 */

const EVENT_KEY_OPEN = 'widget-open';
const EVENT_KEY_CLICKTHROUGH = 'widget-clickthrough';
const EVENT_KEY_CONVERSION = 'widget-conversion';
const EVENT_KEY_CONVERSION_PARTIAL = 'widget-conversion-partial';
const EVENT_KEY_DISMISSAL = 'widget-dismissal';
const EVENT_KEY_SHARE = 'widget-share';
const EVENT_KEY_COPY_COUPON = 'widget-copy-coupon';
const EVENT_KEY_SURVEY_RESPONSE = 'widget-survey-response';
const EVENT_KEY_SURVEY_RESPONSE_PARTIAL = 'widget-survey-response-partial';

const SERIES_OPEN = 'open';
const SERIES_DISMISSAL = 'dismissal';
const SERIES_CONVERSION = 'conversion';
const SERIES_CONVERSION_PARTIAL = 'conversionPartial';
const SERIES_CLICKTHROUGH = 'clickthrough';
const SERIES_SURVEY_RESPONSES = 'surveyResponses';
const SERIES_SURVEY_RESPONSES_PARTIAL = 'surveyResponsesPartial';
const SERIES_SHARE = 'share';
const SERIES_COUPONS_COPIED = 'couponsCopied';

const SERIES_LAST_TOUCH_REVENUE = 'LastTouchRevenue';
const SERIES_LAST_TOUCH_PURCHASES = 'LastTouchPurchases';
const SERIES_INCENTIVIZED_REVENUE = 'IncentivizedRevenue';
const SERIES_INCENTIVIZED_PURCHASES = 'IncentivizedPurchases';

const EVENT_KEY_TO_DAILY_SERIES: Array<[string, string]> = [
  [EVENT_KEY_OPEN, SERIES_OPEN],
  [EVENT_KEY_DISMISSAL, SERIES_DISMISSAL],
  [EVENT_KEY_CONVERSION, SERIES_CONVERSION],
  [EVENT_KEY_CONVERSION_PARTIAL, SERIES_CONVERSION_PARTIAL],
  [EVENT_KEY_CLICKTHROUGH, SERIES_CLICKTHROUGH],
  [EVENT_KEY_SURVEY_RESPONSE, SERIES_SURVEY_RESPONSES],
  [EVENT_KEY_SURVEY_RESPONSE_PARTIAL, SERIES_SURVEY_RESPONSES_PARTIAL],
  [EVENT_KEY_SHARE, SERIES_SHARE],
  [EVENT_KEY_COPY_COUPON, SERIES_COUPONS_COPIED],
];

// ----- DataPlatform request/response types (subset used by migration) --------

export interface DpEventFilter {
  siteIds: number[];
  from: string;
  to?: string;
  widgetIds?: number[];
}

export interface DpWidgetMetricsRequest {
  filter: DpEventFilter;
}

export interface DpWidgetMetricsItem {
  widgetId: number;
  eventCounts: Record<string, number>;
  goalCounts: Record<string, number>;
  revenue: number;
}

export interface DpWidgetMetricsResponse {
  items: DpWidgetMetricsItem[];
}

export interface DpWidgetMetricsDailyRequest {
  filter: DpEventFilter;
  fillEmptyDays?: boolean;
}

export interface DpWidgetMetricsDailyItem {
  widgetId: number;
  day: string;
  eventCounts: Record<string, number>;
  goalCounts: Record<string, number>;
  revenue: number;
}

export interface DpWidgetMetricsDailyResponse {
  items: DpWidgetMetricsDailyItem[];
}

export interface DpWidgetAttributionRequest {
  filter: DpEventFilter;
}

export interface DpWidgetAttributionDailyRequest {
  filter: DpEventFilter;
  fillEmptyDays?: boolean;
}

export interface DpWidgetAttributionDailyItem {
  widgetId: number;
  day: string;
  lastTouchRevenue: number;
  lastTouchPurchases: number;
  incentivizedRevenue: number;
  incentivizedPurchases: number;
}

export interface DpWidgetAttributionDailyResponse {
  items: DpWidgetAttributionDailyItem[];
}

export interface DpWidgetFunnelItem {
  widgetId: number;
  viewedStateIds: Record<number, number>;
}

export interface DpWidgetFunnelResponse {
  items: DpWidgetFunnelItem[];
}

// ----- Request shaping -------------------------------------------------------

export interface DateWindow {
  from: string;
  to: string;
}

/**
 * Mirrors DataPlatform's DateWindowValidation.MaxDailyWindowDays — daily
 * endpoints reject inclusive windows wider than this. The legacy Gateway
 * daily endpoints had no cap, so ranges that legacy flows can produce (e.g.
 * "All time" on a campaign older than a year) must be split with
 * splitIntoDailyWindows and the responses recombined with mergeDailyResponses.
 */
export const MAX_DAILY_WINDOW_DAYS = 366;

export function splitIntoDailyWindows(
  startDate: string | Date,
  endDate: string | Date,
  maxDays: number = MAX_DAILY_WINDOW_DAYS,
): DateWindow[] {
  const startDay = toDayUtc(startDate);
  const endDay = toDayUtc(endDate);
  const totalDays = diffDaysUtc(startDay, endDay) + 1;
  if (totalDays <= maxDays) {
    return [{ from: toIsoString(startDate), to: toIsoString(endDate) }];
  }
  const windows: DateWindow[] = [];
  for (let cursor = startDay; cursor <= endDay; cursor = addDaysUtc(cursor, maxDays)) {
    const windowEndDay = minUtc(addDaysUtc(cursor, maxDays - 1), endDay);
    const isFirst = cursor.getTime() === startDay.getTime();
    const isLast = windowEndDay.getTime() === endDay.getTime();
    windows.push({
      from: isFirst ? toIsoString(startDate) : `${formatDayUtc(cursor)}T00:00:00.000Z`,
      to: isLast ? toIsoString(endDate) : `${formatDayUtc(windowEndDay)}T23:59:59.999Z`,
    });
  }
  return windows;
}

export function mergeDailyResponses<TItem>(responses: Array<{ items: TItem[] }>): { items: TItem[] } {
  return { items: responses.flatMap((response) => response.items) };
}

export function buildEventFilter(
  siteIds: number[],
  startDate: string | Date,
  endDate: string | Date,
  widgetIds?: number[],
): DpEventFilter {
  const filter: DpEventFilter = {
    siteIds,
    from: toIsoString(startDate),
    to: toIsoString(endDate),
  };
  if (widgetIds && widgetIds.length > 0) {
    filter.widgetIds = widgetIds;
  }
  return filter;
}

export function distinctWidgetIds(widgetIdGroups: Array<number[]>): number[] {
  return [...new Set(widgetIdGroups.flat())].filter((id) => id > 0);
}

function toIsoString(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : value;
}

// ----- Per-widget metrics → legacy widgets-statistics ------------------------

export function mapMetricsToWidgetsStatistics(response: DpWidgetMetricsResponse): WidgetStatisticsResponse {
  const data: WidgetStatistics[] = response.items.map((item) => ({
    id: item.widgetId,
    open: readCount(item.eventCounts, EVENT_KEY_OPEN),
    dismissal: readCount(item.eventCounts, EVENT_KEY_DISMISSAL),
    conversion: readCount(item.eventCounts, EVENT_KEY_CONVERSION),
    conversionPartial: readCount(item.eventCounts, EVENT_KEY_CONVERSION_PARTIAL),
    clickThrough: readCount(item.eventCounts, EVENT_KEY_CLICKTHROUGH),
    surveyResponses: readCount(item.eventCounts, EVENT_KEY_SURVEY_RESPONSE),
    surveyResponsesPartial: readCount(item.eventCounts, EVENT_KEY_SURVEY_RESPONSE_PARTIAL),
    share: readCount(item.eventCounts, EVENT_KEY_SHARE),
    couponsCopied: readCount(item.eventCounts, EVENT_KEY_COPY_COUPON),
  }));
  return { error: undefined, data };
}

// ----- Per-(widget,day) metrics → legacy dates-graph -------------------------

export function mapMetricsDailyToDatesGraph(
  response: DpWidgetMetricsDailyResponse,
  startDate: string | Date,
  endDate: string | Date,
  fillEmptyDates: boolean,
): WidgetDailyStatisticsResponse {
  const totalsBySeriesAndDay = new Map<string, Map<string, number>>();
  for (const [, series] of EVENT_KEY_TO_DAILY_SERIES) {
    totalsBySeriesAndDay.set(series, new Map());
  }
  for (const row of response.items) {
    const dayKey = normaliseDayKey(row.day);
    for (const [eventKey, seriesName] of EVENT_KEY_TO_DAILY_SERIES) {
      const value = row.eventCounts[eventKey];
      if (value === undefined || value === 0) {
        continue;
      }
      const seriesMap = totalsBySeriesAndDay.get(seriesName)!;
      seriesMap.set(dayKey, (seriesMap.get(dayKey) ?? 0) + value);
    }
  }
  const items = EVENT_KEY_TO_DAILY_SERIES.map(([, seriesName]) =>
    buildSeries(seriesName, totalsBySeriesAndDay.get(seriesName)!, startDate, endDate, fillEmptyDates),
  );
  return {
    data: {
      name: 'widgets',
      count: items.reduce((sum, s) => sum + (s.count ?? 0), 0),
      items,
    },
  };
}

// ----- Per-(widget,day) attribution → legacy revenue-graph -------------------

export function mapAttributionDailyToRevenueGraph(
  response: DpWidgetAttributionDailyResponse,
  startDate: string | Date,
  endDate: string | Date,
  fillEmptyDates: boolean,
): WidgetDailyStatisticsResponse {
  const totalsBySeriesAndDay = new Map<string, Map<string, number>>([
    [SERIES_LAST_TOUCH_REVENUE, new Map()],
    [SERIES_LAST_TOUCH_PURCHASES, new Map()],
    [SERIES_INCENTIVIZED_REVENUE, new Map()],
    [SERIES_INCENTIVIZED_PURCHASES, new Map()],
  ]);
  for (const row of response.items) {
    const dayKey = normaliseDayKey(row.day);
    accumulate(totalsBySeriesAndDay, SERIES_LAST_TOUCH_REVENUE, dayKey, row.lastTouchRevenue);
    accumulate(totalsBySeriesAndDay, SERIES_LAST_TOUCH_PURCHASES, dayKey, row.lastTouchPurchases);
    accumulate(totalsBySeriesAndDay, SERIES_INCENTIVIZED_REVENUE, dayKey, row.incentivizedRevenue);
    accumulate(totalsBySeriesAndDay, SERIES_INCENTIVIZED_PURCHASES, dayKey, row.incentivizedPurchases);
  }
  const seriesOrder = [
    SERIES_LAST_TOUCH_REVENUE,
    SERIES_LAST_TOUCH_PURCHASES,
    SERIES_INCENTIVIZED_REVENUE,
    SERIES_INCENTIVIZED_PURCHASES,
  ];
  const items = seriesOrder.map((name) =>
    buildSeries(name, totalsBySeriesAndDay.get(name)!, startDate, endDate, fillEmptyDates),
  );
  return {
    data: {
      name: 'widgets-revenue',
      count: items.reduce((sum, s) => sum + (s.count ?? 0), 0),
      items,
    },
  };
}

// ----- Batched per-group splitting ------------------------------------------

export function splitDailyMetricsPerGroup(
  response: DpWidgetMetricsDailyResponse,
  widgetIdGroups: Array<number[]>,
  startDate: string | Date,
  endDate: string | Date,
  fillEmptyDates = true,
): WidgetDailyStatisticsResponse[] {
  const byWidget = groupByWidgetId(response.items);
  return widgetIdGroups.map((widgetIds) =>
    mapMetricsDailyToDatesGraph({ items: collectGroupItems(byWidget, widgetIds) }, startDate, endDate, fillEmptyDates),
  );
}

function groupByWidgetId<T extends { widgetId: number }>(items: T[]): Map<number, T[]> {
  const byWidget = new Map<number, T[]>();
  for (const item of items) {
    const bucket = byWidget.get(item.widgetId);
    if (bucket) {
      bucket.push(item);
    } else {
      byWidget.set(item.widgetId, [item]);
    }
  }
  return byWidget;
}

// Dedupe within a group so a repeated id can't double-count.
function collectGroupItems<T>(byWidget: Map<number, T[]>, widgetIds: number[]): T[] {
  const items: T[] = [];
  const seen = new Set<number>();
  for (const widgetId of widgetIds) {
    if (seen.has(widgetId)) {
      continue;
    }
    seen.add(widgetId);
    const bucket = byWidget.get(widgetId);
    if (bucket) {
      items.push(...bucket);
    }
  }
  return items;
}

// ----- helpers ---------------------------------------------------------------

function readCount(eventCounts: Record<string, number>, key: string): number {
  return eventCounts[key] ?? 0;
}

function accumulate(store: Map<string, Map<string, number>>, series: string, dayKey: string, value: number): void {
  if (!value) {
    return;
  }
  const seriesMap = store.get(series)!;
  seriesMap.set(dayKey, (seriesMap.get(dayKey) ?? 0) + value);
}

interface BuiltSeries {
  name: string;
  count: number;
  items: Array<{ date: string; count: number }>;
}

export function buildSeries(
  name: string,
  dayTotals: Map<string, number>,
  startDate: string | Date,
  endDate: string | Date,
  fillEmptyDates: boolean,
): BuiltSeries {
  const days: Array<{ date: string; count: number }> = [];
  if (fillEmptyDates) {
    for (const dayKey of enumerateDayKeys(startDate, endDate)) {
      days.push({ date: dayKey, count: dayTotals.get(dayKey) ?? 0 });
    }
  } else {
    const sortedKeys = Array.from(dayTotals.keys()).sort();
    for (const dayKey of sortedKeys) {
      days.push({ date: dayKey, count: dayTotals.get(dayKey) ?? 0 });
    }
  }
  const count = days.reduce((sum, d) => sum + d.count, 0);
  return { name, count, items: days };
}

export function normaliseDayKey(day: string): string {
  // DataPlatform returns ISO datetimes with day precision (e.g. 2025-04-12T00:00:00Z).
  // Truncate to YYYY-MM-DD so equality across timezones is stable.
  return day.length >= 10 ? day.substring(0, 10) : day;
}

function* enumerateDayKeys(startDate: string | Date, endDate: string | Date): Generator<string> {
  const start = toDayUtc(startDate);
  const end = toDayUtc(endDate);
  for (let cursor = start; cursor <= end; cursor = addDayUtc(cursor)) {
    yield formatDayUtc(cursor);
  }
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function toDayUtc(value: string | Date): Date {
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function addDayUtc(value: Date): Date {
  return addDaysUtc(value, 1);
}

function addDaysUtc(value: Date, days: number): Date {
  return new Date(value.getTime() + days * DAY_IN_MS);
}

function diffDaysUtc(fromDay: Date, toDay: Date): number {
  return Math.round((toDay.getTime() - fromDay.getTime()) / DAY_IN_MS);
}

function minUtc(a: Date, b: Date): Date {
  return a.getTime() <= b.getTime() ? a : b;
}

function formatDayUtc(value: Date): string {
  const y = value.getUTCFullYear();
  const m = String(value.getUTCMonth() + 1).padStart(2, '0');
  const d = String(value.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
